import { useState, useEffect } from 'react';
import axios from 'axios';
import { Webhook, Plus, Trash2 } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function WebhookManager() {
  const [webhooks, setWebhooks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', url: '', events: [], secret: '' });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWebhooks();
  }, []);

  const fetchWebhooks = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE}/dialer/webhooks`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setWebhooks(response.data);
    } catch (error) {
      console.error('Error fetching webhooks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE}/dialer/webhooks`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchWebhooks();
      setShowForm(false);
      setFormData({ name: '', url: '', events: [], secret: '' });
    } catch (error) {
      console.error('Error creating webhook:', error);
      alert('Failed to create webhook');
    }
  };

  const eventOptions = [
    'call:initiated', 'call:answered', 'call:ended',
    'call:transferred', 'agent:status:changed', 'contact:updated'
  ];

  return (
    <div className="bg-white rounded-lg shadow p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-semibold flex items-center gap-2">
          <Webhook className="w-6 h-6" />
          Webhook Management
        </h3>
        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Webhook
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="border rounded p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">URL</label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full border rounded px-3 py-2"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Events</label>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {eventOptions.map((event) => (
                <label key={event} className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.events.includes(event)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, events: [...formData.events, event] });
                      } else {
                        setFormData({ ...formData, events: formData.events.filter(e => e !== event) });
                      }
                    }}
                  />
                  <span className="text-sm">{event}</span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Secret (optional)</label>
            <input
              type="text"
              value={formData.secret}
              onChange={(e) => setFormData({ ...formData, secret: e.target.value })}
              className="w-full border rounded px-3 py-2"
            />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
              Create
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setFormData({ name: '', url: '', events: [], secret: '' });
              }}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {loading ? (
        <div className="text-center py-4">Loading webhooks...</div>
      ) : webhooks.length === 0 ? (
        <div className="text-center py-8 text-gray-500">No webhooks configured</div>
      ) : (
        <div className="space-y-2">
          {webhooks.map((webhook) => (
            <div key={webhook.id} className="border rounded p-3 flex items-center justify-between">
              <div>
                <p className="font-semibold">{webhook.name}</p>
                <p className="text-sm text-gray-600">{webhook.url}</p>
                <p className="text-xs text-gray-500 mt-1">
                  Events: {webhook.events?.join(', ') || 'None'}
                </p>
              </div>
              <button className="text-red-600 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

